import { useQueryHelper } from '@/@diffrag/hooks/useQueryHelper'
import { useTableSort } from '@/@diffrag/hooks/useTableSort'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { toast } from '@/lib/toast'
import classNames from 'classnames'
import { ChevronDownIcon, MinusIcon, SortAsc, SortDesc, X } from 'lucide-react'
import { useCallback, useContext, useMemo, useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FormContext } from '../form/FormProvider'
import { Icon, IconName } from '../Icon'
import { SearchBar } from '../SearchBar'
import Spinner from '../Spinner'
import { SimpleTableCellRenderer } from './SimpleTableCellRenderer'
import { SimpleTableReorder } from './SimpleTableReorderTableCell'
import { Column } from './type'
import { SimpleTableFilter, TableFilter } from './SimpleTableFilter'
import { useQueryParams } from '../hooks/useQueryParams'

type ActionButton<T> = {
  icon?: IconName
  label?: string
  onClick?: (row: T) => void
}

type TableButton = {
  icon?: IconName
  label?: string
  onClick?: () => void
  href?: string
}

type Props<T> = {
  columns: Column<T>[]
  data?: T[]
  rowButtons?: ActionButton<T>[]
  options?: {
    disableNew?: boolean
    disableSearch?: boolean
    disableEdit?: boolean
    disableSelect?: boolean
    disableSort?: boolean
    tableButtons?: TableButton[]
    searchPlaceholder?: string
  }
  allSelect?: boolean
  filters?: TableFilter[]
  rowStyle?: {
    background?: ((v: T) => string | undefined) | string | undefined
  }
  isLoading?: boolean
  createHref?: string
  totalCount?: number
  onReorder?: (
    row: T,
    direction: 'UP' | 'DOWN' | 'TOP' | 'BOTTOM',
  ) => Promise<void>
  onDelete?: (rows: T[]) => Promise<void>
  onRowClick?: (row: T) => void
  treeOptions?: {
    onToggle?: (row: T, isOpen: boolean) => void
    openedRows?: number[]
  }
  searchParamKey?: string
  deleteText?: string
}

export const SimpleTable = <T,>({
  columns,
  options = {
    disableNew: false,
    disableSearch: false,
    disableSelect: false,
    disableSort: false,
    searchPlaceholder: '검색',
  },
  allSelect,
  filters,
  rowButtons = [],
  data = [],
  rowStyle = {},
  isLoading = false,
  createHref,
  totalCount,
  onReorder,
  onDelete,
  onRowClick,
  treeOptions,
  searchParamKey,
  deleteText = '삭제',
}: Props<T>) => {
  const context = useContext(FormContext)
  const navigate = useNavigate()

  const { query } = useQueryHelper()
  const { query: query2 } = useQueryParams({})

  const { sort, removeSort, sortArray } = useTableSort()

  const fields = context.formProps.fields
  const isShowForm = fields.length > 0

  const [checkedRows, setCheckedRows] = useState<T[]>([])
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false)

  // 검색/필터/페이지 파라미터가 변경될 때 선택된 항목 해제
  useEffect(() => {
    if (data?.length > 0) {
      setCheckedRows([])
    }
  }, [data?.length])

  const check = useCallback(
    (row: T) => {
      if (checkedRows.includes(row)) {
        setCheckedRows(checkedRows.filter((r) => r !== row))
      } else {
        setCheckedRows([...checkedRows, row])
      }
    },
    [checkedRows],
  )

  const checkAll = () => {
    if (checkedRows.length === data.length) {
      setCheckedRows([])
    } else {
      setCheckedRows(data)
    }
  }

  const onClickEdit = useCallback(
    (row: T) => {
      context.showDialog(true, row)
    },
    [context],
  )

  const onClickCreate = () => {
    // context.showDialog(true)
    if (createHref) {
      navigate(createHref)
    }
  }

  const onClickDelete = async () => {
    try {
      await onDelete?.(checkedRows)
      toast.success(
        `${checkedRows.length}개의 데이터가 ${deleteText}되었습니다.`,
      )
      setCheckedRows([])
      setIsOpenDeleteDialog(false)
    } catch (error) {
      toast.error(`${deleteText}에 실패했습니다.`)
    }
  }

  const realButtons: ActionButton<T>[] = useMemo(() => {
    if (!isShowForm) {
      return rowButtons
    }

    if (options.disableEdit) {
      return rowButtons
    }

    return rowButtons
  }, [isShowForm, rowButtons, options.disableEdit])

  const rowBackground = useCallback(
    (row: T) => {
      return typeof rowStyle.background === 'string'
        ? rowStyle.background
        : rowStyle.background?.(row)
    },
    [rowStyle],
  )

  const isShowCreateNew =
    (options.disableNew !== true || createHref) && !query.popup
  const isShowActionBar = !options.disableSearch || isShowCreateNew
  const isShowSelect = !options.disableSelect && !query.popup

  const renderContent = useCallback(() => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell
            colSpan={
              columns.length +
              (isShowSelect ? 1 : 0) +
              (treeOptions?.onToggle ? 1 : 0) +
              (onReorder ? 1 : 0) +
              (realButtons.length > 0 ? 1 : 0)
            }
            className="py-16"
          >
            <div className="flex items-center justify-center">
              <Spinner />
            </div>
          </TableCell>
        </TableRow>
      )
    }

    if (!data?.length) {
      return (
        <TableRow>
          <TableCell
            colSpan={
              columns.length +
              (isShowSelect ? 1 : 0) +
              (treeOptions?.onToggle ? 1 : 0) +
              (onReorder ? 1 : 0) +
              (realButtons.length > 0 ? 1 : 0)
            }
            className="space-x-2 py-16 text-center text-muted-foreground"
          >
            <Icon
              name="info"
              className="mx-auto -mt-1 inline size-5 align-middle"
            />
            <span className="text-base">검색 결과가 없습니다.</span>
          </TableCell>
        </TableRow>
      )
    }

    return data
      .flatMap((row) => {
        if (!treeOptions) {
          return row
        }

        const getChildren = (r: T): T[] => {
          if (!r.children?.length) return []
          return r.children.flatMap((child) => [child, ...getChildren(child)])
        }
        return [row, ...getChildren(row)]
      })
      .map((row) => {
        const isChecked = checkedRows.includes(row)

        const isOpened = treeOptions?.openedRows?.includes(row.id)

        return (
          <TableRow
            key={row.id}
            style={{
              background: (rowBackground(row) ?? isChecked) ? '#fcf8e3' : '',
            }}
            className={onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}
            onClick={() => onRowClick?.(row)}
          >
            {isShowSelect && (
              <TableCell className="w-12 py-0">
                <div className="flex">
                  <Checkbox
                    checked={isChecked}
                    onClick={(e) => {
                      e.stopPropagation()
                      check(row)
                    }}
                  />
                </div>
              </TableCell>
            )}

            {treeOptions?.onToggle && (
              <TableCell className="w-12 py-0">
                <div className="flex">
                  <Button
                    variant="outline"
                    size="icon"
                    className="size-6"
                    onClick={(e) => {
                      e.stopPropagation()
                      treeOptions.onToggle?.(row, !isOpened)
                    }}
                  >
                    {isOpened ? (
                      <MinusIcon className="size-3" />
                    ) : (
                      <ChevronDownIcon className="size-4" />
                    )}
                  </Button>
                </div>
              </TableCell>
            )}

            {onReorder && (
              <SimpleTableReorder.TableCell onReorder={onReorder} row={row} />
            )}

            {columns.map((c, i) => {
              return (
                <SimpleTableCellRenderer
                  key={c.key.toString()}
                  data={row}
                  column={c}
                  cellIndex={i}
                  cellStyle={{
                    paddingLeft:
                      i === 0 && typeof row.level === 'number' && treeOptions
                        ? row.level * 15
                        : undefined,
                  }}
                />
              )
            })}
            {realButtons.length > 0 && (
              <TableCell className="space-x-2 whitespace-nowrap py-0 text-right">
                {realButtons.map((button, i) => {
                  return (
                    <Button
                      key={i}
                      variant="outline"
                      size="icon"
                      type="button"
                      className={classNames(`size-9 text-muted-foreground`, {
                        'w-fit px-2 gap-0.5': !!button.label,
                      })}
                      onClick={(e) => {
                        e.stopPropagation()
                        button.onClick?.(row)
                      }}
                    >
                      {button.icon && <Icon name={button.icon} size={14} />}
                      {button.label && (
                        <span className="text-xs">{button.label}</span>
                      )}
                    </Button>
                  )
                })}
              </TableCell>
            )}
          </TableRow>
        )
      })
  }, [
    data,
    columns,
    checkedRows,
    isLoading,
    rowBackground,
    realButtons,
    onReorder,
    check,
    isShowSelect,
    treeOptions,
  ])

  return (
    <div className="space-y-3">
      {filters?.length && (
        <SimpleTableFilter filters={filters} allSelect={allSelect} />
      )}
      {isShowActionBar && (
        <div className="relative flex w-full items-center justify-between gap-3">
          {!options.disableSearch && (
            <SearchBar
              searchParamKey={searchParamKey}
              placeholder={options.searchPlaceholder}
            />
          )}

          <div className="flex items-center gap-2">
            {options.tableButtons?.map((button, i) => {
              if (button.href) {
                return (
                  <Link to={button.href} key={i}>
                    <Button size="sm">{button.label}</Button>
                  </Link>
                )
              }

              return (
                <Button key={i} size="sm" onClick={button.onClick}>
                  {button.label}
                </Button>
              )
            })}

            {checkedRows.length > 0 && onDelete && (
              <div className="-my-4">
                <Dialog
                  open={isOpenDeleteDialog}
                  onOpenChange={setIsOpenDeleteDialog}
                >
                  <DialogTrigger>
                    <Button
                      size="sm"
                      onClick={() => setIsOpenDeleteDialog(true)}
                      variant="destructive"
                    >
                      {checkedRows.length}개 {deleteText}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{deleteText} 확인</DialogTitle>
                    </DialogHeader>
                    <DialogDescription>
                      {checkedRows.length}개를 {deleteText}하시겠습니까?
                    </DialogDescription>
                    <DialogFooter>
                      <DialogClose>
                        <Button variant="outline">취소</Button>
                      </DialogClose>
                      <Button onClick={onClickDelete} variant="destructive">
                        {deleteText}하기
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            )}
            {isShowCreateNew && (
              <Button size="sm" onClick={onClickCreate}>
                새로 만들기
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="flex min-h-8 items-center gap-2">
        <div className="flex h-5 gap-2">
          {typeof totalCount === 'number' && (
            <p className="text-sm font-medium text-neutral-500">
              전체: {totalCount}
            </p>
          )}
          {checkedRows.length > 0 && (
            <p className="text-sm font-medium text-neutral-500">
              ({checkedRows.length} 선택됨)
            </p>
          )}
        </div>

        {sortArray.length > 0 && !options.disableSort && (
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-neutral-500">| 정렬:</p>
            {sortArray.map((v, i) => {
              const label = columns.find(
                (f) => f.key === v || `-${f.key}` === v,
              )?.label

              return (
                <Badge
                  key={v}
                  variant="outline"
                  className="flex h-7 items-center gap-1 bg-white"
                >
                  <span>
                    {i + 1}. {label}
                  </span>
                  {v.startsWith('-') ? (
                    <SortAsc className="size-4" />
                  ) : (
                    <SortDesc className="size-4" />
                  )}
                  <X
                    className="size-4 cursor-pointer"
                    onClick={() => removeSort(v)}
                  />
                </Badge>
              )
            })}
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded border">
        <Table className="min-w-full table-fixed bg-white">
          {!!columns?.length && (
            <TableHeader>
              <TableRow>
                {isShowSelect && (
                  <TableHead className="h-8 w-12 bg-blue-500/80 hover:bg-blue-500/85">
                    <div className="flex">
                      <Checkbox
                        checked={checkedRows.length === data.length}
                        onClick={checkAll}
                      />
                    </div>
                  </TableHead>
                )}
                {treeOptions?.onToggle && (
                  <TableHead className="h-8 w-12 bg-blue-500/80 hover:bg-blue-500/85"></TableHead>
                )}
                {onReorder && (
                  <TableHead className="h-8 w-16 bg-blue-500/80 hover:bg-blue-500/85"></TableHead>
                )}
                {columns.map((c) => {
                  const isSorted =
                    sortArray.includes(`-${c.key}`) || sortArray.includes(c.key)

                  const sortIndex = sortArray.findIndex(
                    (v) => v === c.key || v === `-${c.key}`,
                  )

                  return (
                    <TableHead
                      key={c.key.toString()}
                      className="h-8 cursor-pointer whitespace-nowrap bg-blue-500/80 text-xs font-semibold text-white hover:bg-blue-500/85"
                      style={{ width: c.width }}
                      onClick={() => {
                        if (options.disableSort) {
                          return
                        }

                        sort(c.key)
                      }}
                    >
                      <div className="flex items-center justify-between gap-1">
                        {c.label}
                        {isSorted && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs font-normal text-white">
                              {sortIndex + 1}
                            </span>
                            {sortArray.includes(`-${c.key}`) ? (
                              <SortAsc className={classNames('size-4')} />
                            ) : (
                              <SortDesc className={classNames('size-4')} />
                            )}
                          </div>
                        )}
                      </div>
                    </TableHead>
                  )
                })}
                {realButtons.length > 0 && (
                  <TableHead className="h-8 w-20 whitespace-nowrap bg-blue-500/80 text-xs font-medium text-white hover:bg-blue-500/85" />
                )}
              </TableRow>
            </TableHeader>
          )}
          <TableBody>{renderContent()}</TableBody>
        </Table>
      </div>
    </div>
  )
}
